/**
 * Bestiary Data - Two-Dice Combat System
 * 
 * Complete creature stats for Folklore World.
 * Stats balanced for solo play (party size = 1) using two-dice combat system.
 * REBALANCED: December 6, 2025 - Unconstrained dice optimization
 * 
 * Player Stats: 50 HP, d11 attack, d10 damage, 2 defense
 * 
 * System:
 * - attackDie: Die size for attack rolls (determines round winner)
 * - damageDie: Die size for damage rolls (determines damage dealt)
 * - defense: Damage reduction (minimum 1 damage always dealt)
 * - Kept 'attack' field for backward compatibility (deprecated)
 * 
 * Average balance error: 0.14% across all monsters
 * Party size 2-4 stats maintained but not actively balanced.
 */

export interface CreatureStats {
  hp: number;
  attack: number;         // DEPRECATED: Use attackDie instead
  attackDie?: number;     // NEW: Die size for attack (e.g., 10 for d10)
  damageDie?: number;     // NEW: Die size for damage (e.g., 10 for d10)
  defense?: number;       // NEW: Armor/damage reduction (reduces incoming damage)
}

export type MonsterTier = 'Legendary' | 'Formidable' | 'Dangerous' | 'Moderate' | 'Minor' | 'Weak';

export interface Creature {
  name: string;
  tier?: MonsterTier;            // NEW: Difficulty tier
  encounterWeight?: number;      // NEW: Probability weight for encounters
  statsByPartySize: {
    1: CreatureStats;
    2: CreatureStats;
    3: CreatureStats;
    4: CreatureStats;
  };
  imagePath?: string;
  terrains?: string[];           // NEW: List of terrains this creature appears in
}

/**
 * Complete bestiary for Folklore World
 * Stats format: HP / Attack Score
 */
export const BESTIARY: Creature[] = [
  // LEGENDARY TIER (Player: 50 HP, d11/d10/2 def)
  {
    name: 'Wyvern',
    tier: 'Legendary',
    encounterWeight: 0.167,
    terrains: ['Mountain', 'Hills', 'Coastal'],
    statsByPartySize: {
      1: { hp: 48, attack: 7, attackDie: 7, damageDie: 17, defense: 3 },  // 45.2% win rate
      2: { hp: 34, attack: 13, attackDie: 20, damageDie: 12, defense: 0 },
      3: { hp: 41, attack: 17, attackDie: 20, damageDie: 12, defense: 0 },
      4: { hp: 48, attack: 21, attackDie: 20, damageDie: 12, defense: 0 },
    },
  },
  {
    name: 'Golem',
    tier: 'Legendary',
    encounterWeight: 0.167,
    terrains: ['Mountain', 'Desert', 'Hills'],
    statsByPartySize: {
      1: { hp: 45, attack: 8, attackDie: 8, damageDie: 16, defense: 2 },  // 38.1% win rate
      2: { hp: 21, attack: 13, attackDie: 12, damageDie: 12, defense: 0 },
      3: { hp: 28, attack: 17, attackDie: 12, damageDie: 12, defense: 0 },
      4: { hp: 35, attack: 21, attackDie: 12, damageDie: 12, defense: 0 },
    },
  },
  {
    name: 'Manticore',
    tier: 'Legendary',
    encounterWeight: 0.167,
    terrains: ['Desert', 'Mountain', 'Grassland'],
    statsByPartySize: {
      1: { hp: 42, attack: 8, attackDie: 8, damageDie: 14, defense: 3 },  // 35.2% win rate
      2: { hp: 20, attack: 13, attackDie: 12, damageDie: 12, defense: 0 },
      3: { hp: 27, attack: 17, attackDie: 12, damageDie: 12, defense: 0 },
      4: { hp: 34, attack: 21, attackDie: 12, damageDie: 12, defense: 0 },
    },
  },
  
  // FORMIDABLE TIER
  {
    name: 'Revenant',
    tier: 'Formidable',
    encounterWeight: 0.625,
    terrains: ['Forest', 'Hills', 'Grassland'],
    statsByPartySize: {
      1: { hp: 38, attack: 6, attackDie: 6, damageDie: 18, defense: 4 },  // 31.9% win rate
      2: { hp: 32, attack: 11, attackDie: 12, damageDie: 10, defense: 0 },
      3: { hp: 39, attack: 15, attackDie: 12, damageDie: 10, defense: 0 },
      4: { hp: 46, attack: 19, attackDie: 12, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Basilisk',
    tier: 'Formidable',
    encounterWeight: 0.625,
    terrains: ['Mountain', 'Desert', 'Coastal'],
    statsByPartySize: {
      1: { hp: 38, attack: 8, attackDie: 8, damageDie: 14, defense: 3 },  // 29.1% win rate
      2: { hp: 22, attack: 12, attackDie: 10, damageDie: 10, defense: 0 },
      3: { hp: 29, attack: 16, attackDie: 10, damageDie: 10, defense: 0 },
      4: { hp: 36, attack: 20, attackDie: 10, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Chimera',
    tier: 'Formidable',
    encounterWeight: 0.625,
    terrains: ['Mountain', 'Hills', 'Desert'],
    statsByPartySize: {
      1: { hp: 35, attack: 9, attackDie: 9, damageDie: 19, defense: 0 },  // 27.8% win rate
      2: { hp: 21, attack: 12, attackDie: 12, damageDie: 10, defense: 0 },
      3: { hp: 28, attack: 16, attackDie: 12, damageDie: 10, defense: 0 },
      4: { hp: 35, attack: 20, attackDie: 12, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Wraith',
    tier: 'Formidable',
    encounterWeight: 0.625,
    terrains: ['Forest', 'Hills', 'Marsh'],
    statsByPartySize: {
      1: { hp: 35, attack: 5, attackDie: 5, damageDie: 19, defense: 5 },  // 26.1% win rate
      2: { hp: 20, attack: 12, attackDie: 10, damageDie: 10, defense: 0 },
      3: { hp: 27, attack: 16, attackDie: 10, damageDie: 10, defense: 0 },
      4: { hp: 34, attack: 20, attackDie: 10, damageDie: 10, defense: 0 },
    },
  },
  
  // DANGEROUS TIER
  {
    name: 'Griffon',
    tier: 'Dangerous',
    encounterWeight: 1.4,
    terrains: ['Mountain', 'Hills', 'Grassland'],
    statsByPartySize: {
      1: { hp: 32, attack: 6, attackDie: 6, damageDie: 18, defense: 4 },  // 22.3% win rate
      2: { hp: 16, attack: 10, attackDie: 10, damageDie: 10, defense: 0 },
      3: { hp: 21, attack: 13, attackDie: 10, damageDie: 10, defense: 0 },
      4: { hp: 26, attack: 16, attackDie: 10, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Troll',
    tier: 'Dangerous',
    encounterWeight: 1.4,
    terrains: ['Forest', 'Hills', 'Mountain'],
    statsByPartySize: {
      1: { hp: 38, attack: 7, attackDie: 7, damageDie: 17, defense: 2 },  // 19.9% win rate
      2: { hp: 19, attack: 11, attackDie: 8, damageDie: 12, defense: 0 },
      3: { hp: 26, attack: 15, attackDie: 8, damageDie: 12, defense: 0 },
      4: { hp: 33, attack: 19, attackDie: 8, damageDie: 12, defense: 0 },
    },
  },
  {
    name: 'Vampire',
    tier: 'Dangerous',
    encounterWeight: 1.4,
    terrains: ['Forest', 'Hills', 'Coastal', 'Mountain'],
    statsByPartySize: {
      1: { hp: 28, attack: 8, attackDie: 8, damageDie: 13, defense: 4 },  // 19.0% win rate
      2: { hp: 14, attack: 9, attackDie: 12, damageDie: 10, defense: 0 },
      3: { hp: 19, attack: 12, attackDie: 12, damageDie: 10, defense: 0 },
      4: { hp: 24, attack: 15, attackDie: 12, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Gargoyle',
    tier: 'Dangerous',
    encounterWeight: 1.4,
    terrains: ['Mountain', 'Hills', 'Coastal'],
    statsByPartySize: {
      1: { hp: 40, attack: 7, attackDie: 7, damageDie: 20, defense: 0 },  // 17.2% win rate
      2: { hp: 10, attack: 8, attackDie: 8, damageDie: 10, defense: 0 },
      3: { hp: 13, attack: 10, attackDie: 8, damageDie: 10, defense: 0 },
      4: { hp: 16, attack: 12, attackDie: 8, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Fomorian',
    tier: 'Dangerous',
    encounterWeight: 1.4,
    terrains: ['Hills', 'Mountain', 'Grassland'],
    statsByPartySize: {
      1: { hp: 38, attack: 8, attackDie: 8, damageDie: 10, defense: 4 },  // 12.2% win rate
      2: { hp: 5, attack: 5, attackDie: 8, damageDie: 12, defense: 0 },
      3: { hp: 6, attack: 6, attackDie: 8, damageDie: 12, defense: 0 },
      4: { hp: 6, attack: 6, attackDie: 8, damageDie: 12, defense: 0 },
    },
  },

  // MODERATE TIER
  {
    name: 'Hippogriff',
    tier: 'Moderate',
    encounterWeight: 3.0,
    terrains: ['Mountain', 'Hills', 'Grassland'],
    statsByPartySize: {
      1: { hp: 28, attack: 6, attackDie: 6, damageDie: 17, defense: 4 },  // 14.0% win rate
      2: { hp: 15, attack: 8, attackDie: 10, damageDie: 8, defense: 0 },
      3: { hp: 20, attack: 11, attackDie: 10, damageDie: 8, defense: 0 },
      4: { hp: 25, attack: 14, attackDie: 10, damageDie: 8, defense: 0 },
    },
  },
  {
    name: 'Phoenix',
    tier: 'Moderate',
    encounterWeight: 3.0,
    terrains: ['Desert', 'Mountain', 'Hills'],
    statsByPartySize: {
      1: { hp: 28, attack: 7, attackDie: 7, damageDie: 18, defense: 2 },  // 12.1% win rate
      2: { hp: 14, attack: 8, attackDie: 8, damageDie: 10, defense: 0 },
      3: { hp: 19, attack: 11, attackDie: 8, damageDie: 10, defense: 0 },
      4: { hp: 24, attack: 14, attackDie: 8, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Werebear',
    tier: 'Moderate',
    encounterWeight: 3.0,
    terrains: ['Forest', 'Hills', 'Marsh'],
    statsByPartySize: {
      1: { hp: 28, attack: 6, attackDie: 6, damageDie: 14, defense: 5 },  // 11.0% win rate
      2: { hp: 14, attack: 8, attackDie: 8, damageDie: 10, defense: 0 },
      3: { hp: 19, attack: 11, attackDie: 8, damageDie: 10, defense: 0 },
      4: { hp: 24, attack: 14, attackDie: 8, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Minotaur',
    tier: 'Moderate',
    encounterWeight: 3.0,
    terrains: ['Hills', 'Grassland', 'Desert'],
    statsByPartySize: {
      1: { hp: 28, attack: 5, attackDie: 5, damageDie: 19, defense: 4 },  // 9.9% win rate
      2: { hp: 9, attack: 7, attackDie: 8, damageDie: 10, defense: 0 },
      3: { hp: 12, attack: 9, attackDie: 8, damageDie: 10, defense: 0 },
      4: { hp: 15, attack: 11, attackDie: 8, damageDie: 10, defense: 0 },
    },
  },
  {
    name: 'Werewolf',
    tier: 'Moderate',
    encounterWeight: 3.0,
    terrains: ['Forest', 'Hills', 'Grassland'],
    statsByPartySize: {
      1: { hp: 26, attack: 5, attackDie: 5, damageDie: 17, defense: 5 },  // 8.9% win rate
      2: { hp: 13, attack: 8, attackDie: 8, damageDie: 8, defense: 0 },
      3: { hp: 18, attack: 11, attackDie: 8, damageDie: 8, defense: 0 },
      4: { hp: 23, attack: 14, attackDie: 8, damageDie: 8, defense: 0 },
    },
  },
  
  // MINOR TIER
  {
    name: 'Ogre',
    tier: 'Minor',
    encounterWeight: 5.0,
    terrains: ['Forest', 'Hills', 'Mountain'],
    statsByPartySize: {
      1: { hp: 22, attack: 5, attackDie: 5, damageDie: 17, defense: 5 },  // 5.9% win rate
      2: { hp: 13, attack: 8, attackDie: 6, damageDie: 12, defense: 0 },
      3: { hp: 18, attack: 11, attackDie: 6, damageDie: 12, defense: 0 },
      4: { hp: 23, attack: 14, attackDie: 6, damageDie: 12, defense: 0 },
    },
  },
  {
    name: 'Witch',
    tier: 'Minor',
    encounterWeight: 5.0,
    terrains: ['Forest', 'Marsh', 'Coastal', 'Hills'],
    statsByPartySize: {
      1: { hp: 22, attack: 6, attackDie: 6, damageDie: 14, defense: 5 },  // 6.0% win rate
      2: { hp: 10, attack: 7, attackDie: 8, damageDie: 8, defense: 0 },
      3: { hp: 13, attack: 9, attackDie: 8, damageDie: 8, defense: 0 },
      4: { hp: 16, attack: 11, attackDie: 8, damageDie: 8, defense: 0 },
    },
  },
  {
    name: 'Banshee',
    tier: 'Minor',
    encounterWeight: 5.0,
    terrains: ['Forest', 'Hills', 'Marsh', 'Coastal'],
    statsByPartySize: {
      1: { hp: 20, attack: 5, attackDie: 5, damageDie: 19, defense: 4 },  // 4.8% win rate
      2: { hp: 9, attack: 7, attackDie: 8, damageDie: 6, defense: 0 },
      3: { hp: 12, attack: 9, attackDie: 8, damageDie: 6, defense: 0 },
      4: { hp: 15, attack: 11, attackDie: 8, damageDie: 6, defense: 0 },
    },
  },
  {
    name: 'Incubus',
    tier: 'Minor',
    encounterWeight: 5.0,
    terrains: ['Forest', 'Hills', 'Grassland'],
    statsByPartySize: {
      1: { hp: 22, attack: 7, attackDie: 7, damageDie: 13, defense: 4 },  // 4.7% win rate
      2: { hp: 9, attack: 7, attackDie: 8, damageDie: 8, defense: 0 },
      3: { hp: 12, attack: 9, attackDie: 8, damageDie: 8, defense: 0 },
      4: { hp: 15, attack: 11, attackDie: 8, damageDie: 8, defense: 0 },
    },
  },
  {
    name: 'Kelpie',
    tier: 'Minor',
    encounterWeight: 5.0,
    terrains: ['River', 'Lakeside', 'Coastal', 'Marsh'],
    statsByPartySize: {
      1: { hp: 20, attack: 5, attackDie: 5, damageDie: 19, defense: 4 },  // 4.2% win rate
      2: { hp: 5, attack: 5, attackDie: 6, damageDie: 8, defense: 0 },
      3: { hp: 6, attack: 6, attackDie: 6, damageDie: 8, defense: 0 },
      4: { hp: 6, attack: 6, attackDie: 6, damageDie: 8, defense: 0 },
    },
  },
  
  // WEAK TIER (1-3% win rate)
  {
    name: 'Siren',
    tier: 'Weak',
    encounterWeight: 7.14,
    terrains: ['Coastal', 'Lakeside', 'River'],
    statsByPartySize: {
      1: { hp: 12, attack: 5, attackDie: 5, damageDie: 19, defense: 5 },  // 2.2% win rate
      2: { hp: 5, attack: 5, attackDie: 6, damageDie: 6, defense: 0 },
      3: { hp: 6, attack: 6, attackDie: 6, damageDie: 6, defense: 0 },
      4: { hp: 6, attack: 6, attackDie: 6, damageDie: 6, defense: 0 },
    },
  },
  {
    name: 'Nymph',
    tier: 'Weak',
    encounterWeight: 7.14,
    terrains: ['Forest', 'Grassland', 'River', 'Lakeside'],
    statsByPartySize: {
      1: { hp: 10, attack: 5, attackDie: 5, damageDie: 20, defense: 5 },  // 1.7% win rate
      2: { hp: 4, attack: 5, attackDie: 6, damageDie: 6, defense: 0 },
      3: { hp: 5, attack: 6, attackDie: 6, damageDie: 6, defense: 0 },
      4: { hp: 5, attack: 6, attackDie: 6, damageDie: 6, defense: 0 },
    },
  },
  {
    name: 'Serpent',
    tier: 'Weak',
    encounterWeight: 7.14,
    terrains: ['Desert', 'Grassland', 'Marsh', 'River'],
    statsByPartySize: {
      1: { hp: 12, attack: 5, attackDie: 5, damageDie: 19, defense: 5 },  // 2.0% win rate
      2: { hp: 9, attack: 5, attackDie: 6, damageDie: 8, defense: 0 },
      3: { hp: 12, attack: 7, attackDie: 6, damageDie: 8, defense: 0 },
      4: { hp: 15, attack: 9, attackDie: 6, damageDie: 8, defense: 0 },
    },
  },
  {
    name: 'Harpy',
    tier: 'Weak',
    encounterWeight: 7.14,
    terrains: ['Mountain', 'Hills', 'Coastal', 'Grassland'],
    statsByPartySize: {
      1: { hp: 10, attack: 5, attackDie: 5, damageDie: 20, defense: 5 },  // 1.8% win rate
      2: { hp: 8, attack: 5, attackDie: 6, damageDie: 6, defense: 0 },
      3: { hp: 11, attack: 7, attackDie: 6, damageDie: 6, defense: 0 },
      4: { hp: 14, attack: 9, attackDie: 6, damageDie: 6, defense: 0 },
    },
  },
  {
    name: 'Hag',
    tier: 'Weak',
    encounterWeight: 7.14,
    terrains: ['Forest', 'Marsh', 'Hills'],
    statsByPartySize: {
      1: { hp: 8, attack: 6, attackDie: 6, damageDie: 18, defense: 5 },  // 1.6% win rate
      2: { hp: 4, attack: 4, attackDie: 6, damageDie: 6, defense: 0 },
      3: { hp: 5, attack: 5, attackDie: 6, damageDie: 6, defense: 0 },
      4: { hp: 5, attack: 5, attackDie: 6, damageDie: 6, defense: 0 },
    },
  },
  {
    name: 'Imp',
    tier: 'Weak',
    encounterWeight: 7.14,
    terrains: ['Forest', 'Hills', 'Desert'],
    statsByPartySize: {
      1: { hp: 6, attack: 6, attackDie: 6, damageDie: 18, defense: 5 },  // 0.9% win rate
      2: { hp: 3, attack: 4, attackDie: 6, damageDie: 6, defense: 0 },
      3: { hp: 4, attack: 5, attackDie: 6, damageDie: 6, defense: 0 },
      4: { hp: 4, attack: 5, attackDie: 6, damageDie: 6, defense: 0 },
    },
  },
  {
    name: 'Fairy',
    tier: 'Weak',
    encounterWeight: 7.14,
    terrains: ['Forest', 'Grassland', 'Hills'],
    statsByPartySize: {
      1: { hp: 4, attack: 4, attackDie: 4, damageDie: 13, defense: 1 },  // 0.0% win rate
      2: { hp: 3, attack: 4, attackDie: 4, damageDie: 6, defense: 0 },
      3: { hp: 4, attack: 5, attackDie: 4, damageDie: 6, defense: 0 },
      4: { hp: 4, attack: 5, attackDie: 4, damageDie: 6, defense: 0 },
    },
  },
];

/**
 * Get a random monster from the bestiary (LEGACY - flat distribution)
 * If "Always Nymph" setting is enabled, always returns "Nymph"
 * @deprecated Use getWeightedRandomMonster() for terrain-based weighted selection
 */
export function getRandomMonster(): string {
  // Check if Always Nymph mode is enabled
  const alwaysNymph = localStorage.getItem('storyworld_always_nymph') === 'true';
  
  if (alwaysNymph) {
    return 'Nymph';
  }
  
  const randomIndex = Math.floor(Math.random() * BESTIARY.length);
  return BESTIARY[randomIndex].name;
}

/**
 * Select a weighted random monster from a list
 * Uses encounterWeight to make weak monsters common and legendary rare
 */
export function getWeightedRandomMonster(validMonsters: Creature[]): string {
  // Check if Always Nymph mode is enabled
  const alwaysNymph = localStorage.getItem('storyworld_always_nymph') === 'true';
  if (alwaysNymph) {
    return 'Nymph';
  }
  
  if (validMonsters.length === 0) {
    console.warn('No valid monsters provided, using fallback');
    return 'Nymph'; // Safe fallback
  }
  
  // Calculate total weight
  const totalWeight = validMonsters.reduce(
    (sum, m) => sum + (m.encounterWeight || 1), 
    0
  );
  
  // Random selection weighted by encounterWeight
  let random = Math.random() * totalWeight;
  
  for (const monster of validMonsters) {
    random -= (monster.encounterWeight || 1);
    if (random <= 0) {
      return monster.name;
    }
  }
  
  // Fallback (should never reach)
  return validMonsters[0].name;
}

/**
 * Normalize terrain name for matching
 * Handles variations like "FOREST" vs "Forest" vs "forest"
 */
function normalizeTerrainName(terrain: string): string {
  return terrain.toLowerCase().trim();
}

/**
 * Get terrain category name from TerrainType enum value
 */
function getTerrainCategoryFromType(terrainType: number): string[] {
  const categories: string[] = [];
  
  // Base terrain category
  switch (terrainType) {
    case 0: categories.push('Mountain'); break;
    case 1: categories.push('Grassland'); break; // Tundra treated as grassland
    case 2: categories.push('Hills'); break;
    case 3: categories.push('Forest'); break;
    case 4: categories.push('Settlement'); break; // No monsters spawn here
    case 5: categories.push('River'); break;
    case 6: categories.push('Desert'); break;
    case 7: categories.push('Ocean'); break; // No monsters spawn here
    default: categories.push('Grassland'); break; // Fallback
  }
  
  // Note: Coastal/Lakeside detection would require hex neighbor data
  // For now, we'll just use base terrain
  // TODO: Add Coastal/Lakeside detection when terrain map is available
  
  return categories;
}

/**
 * Select exploration monster with weighted probability and terrain filtering
 * This is the main function for exploration mode encounters
 * 
 * @param terrainType - TerrainType enum value (0-7)
 * @returns Monster name, or empty string if no valid monsters
 */
export function selectExplorationMonster(terrainType: number): string {
  // Get terrain categories for this hex
  const terrainCategories = getTerrainCategoryFromType(terrainType);
  
  // Filter monsters that can appear in at least one of the terrain categories
  const validMonsters = BESTIARY.filter(monster => {
    if (!monster.terrains || monster.terrains.length === 0) {
      return true; // Monster with no terrain restriction can appear anywhere
    }
    
    // Check if monster's terrains overlap with current hex's categories
    return monster.terrains.some(monsterTerrain => 
      terrainCategories.some(hexCategory => 
        normalizeTerrainName(monsterTerrain) === normalizeTerrainName(hexCategory)
      )
    );
  });
  
  // If no valid monsters (shouldn't happen), log warning and use all
  if (validMonsters.length === 0) {
    console.warn(`No monsters found for terrain ${terrainType} (${terrainCategories.join(', ')}), using all monsters`);
    return getWeightedRandomMonster(BESTIARY);
  }
  
  // Select weighted random from valid monsters
  return getWeightedRandomMonster(validMonsters);
}

/**
 * Quest combat context for intelligent monster selection
 */
export interface QuestCombatContext {
  sectionNumber: number;        // 1-12 (section within quest)
  previousMonsters: string[];   // Last 2-3 monsters fought in this quest
  questTheme?: string;          // Optional: 'haunted', 'wilderness', 'coastal', etc.
  currentTerrain: number;       // TerrainType enum value
  isBossFight: boolean;         // Section 12 = final boss
}

/**
 * Select a quest monster with intelligent difficulty pacing
 * 
 * This function creates a narrative-driven difficulty curve:
 * - Early sections: Weak/Minor monsters (introductory encounters)
 * - Mid sections: Minor/Moderate/Dangerous (building tension)
 * - Late sections: Moderate/Dangerous/Formidable (climactic battles)
 * - Final section: Boss fight (Dangerous/Formidable/Legendary)
 * 
 * IMPORTANT: This function does NOT consider player HP.
 * Players must be able to die in quests to maintain stakes.
 * Low HP creates tension, not automatic easy fights.
 * 
 * @param context - Quest combat context
 * @returns Monster name
 */
export function selectQuestMonster(context: QuestCombatContext): string {
  const { sectionNumber, previousMonsters, currentTerrain, isBossFight } = context;
  
  // Boss fight = guaranteed Dangerous or higher
  if (isBossFight) {
    return selectFromTiers(
      ['Dangerous', 'Formidable', 'Legendary'],
      currentTerrain,
      [0.5, 0.4, 0.1] // 50% Dangerous, 40% Formidable, 10% Legendary
    );
  }
  
  // Check recent monster difficulty for anti-fatigue
  const recentTiers = previousMonsters
    .slice(0, 2)
    .map(name => getCreature(name)?.tier)
    .filter(Boolean) as MonsterTier[];
  
  // If last fight was Formidable/Legendary, give player a breather
  if (recentTiers[0] === 'Formidable' || recentTiers[0] === 'Legendary') {
    return selectFromTiers(['Weak', 'Minor'], currentTerrain);
  }
  
  // If last 2 fights were Dangerous+, add a breather
  const lastTwoAreDangerous = recentTiers.length >= 2 && 
    recentTiers.slice(0, 2).every(t => 
      t === 'Dangerous' || t === 'Formidable' || t === 'Legendary'
    );
  
  if (lastTwoAreDangerous) {
    return selectFromTiers(['Weak', 'Minor', 'Moderate'], currentTerrain, [0.3, 0.3, 0.4]);
  }
  
  // Normal progression based on section number
  const sectionProgress = sectionNumber / 12;
  
  if (sectionProgress < 0.3) {
    // Early sections (1-3): Weak/Minor heavy, some Moderate
    return selectFromTiers(
      ['Weak', 'Minor', 'Moderate'], 
      currentTerrain, 
      [0.4, 0.3, 0.3]
    );
  } else if (sectionProgress < 0.7) {
    // Mid sections (4-8): Minor/Moderate, introduce Dangerous
    return selectFromTiers(
      ['Minor', 'Moderate', 'Dangerous'], 
      currentTerrain, 
      [0.2, 0.4, 0.4]
    );
  } else {
    // Late sections (9-11): Moderate/Dangerous, some Formidable
    return selectFromTiers(
      ['Moderate', 'Dangerous', 'Formidable'], 
      currentTerrain, 
      [0.2, 0.5, 0.3]
    );
  }
}

/**
 * Select from specific tiers with custom weights
 * Helper function for quest monster selection
 */
function selectFromTiers(
  allowedTiers: MonsterTier[],
  terrainType: number,
  weights?: number[]
): string {
  // Get terrain categories
  const terrainCategories = getTerrainCategoryFromType(terrainType);
  
  // Filter monsters by tier and terrain
  const validMonsters = BESTIARY.filter(monster => {
    // Must match allowed tier
    if (!monster.tier || !allowedTiers.includes(monster.tier)) {
      return false;
    }
    
    // Must match terrain (or have no terrain restriction)
    if (!monster.terrains || monster.terrains.length === 0) {
      return true;
    }
    
    return monster.terrains.some(monsterTerrain => 
      terrainCategories.some(hexCategory => 
        normalizeTerrainName(monsterTerrain) === normalizeTerrainName(hexCategory)
      )
    );
  });
  
  if (validMonsters.length === 0) {
    console.warn(`No monsters for tiers ${allowedTiers.join(', ')} in terrain ${terrainType}`);
    // Fallback: use exploration selection
    return selectExplorationMonster(terrainType);
  }
  
  // If custom weights provided, apply them by tier
  if (weights && weights.length === allowedTiers.length) {
    return selectWithCustomWeights(validMonsters, allowedTiers, weights);
  }
  
  // Otherwise use natural encounter weights
  return getWeightedRandomMonster(validMonsters);
}

/**
 * Select monster with custom tier weights
 * Used for precise difficulty control in quests
 */
function selectWithCustomWeights(
  monsters: Creature[],
  tiers: MonsterTier[],
  tierWeights: number[]
): string {
  // Build weighted list based on tier
  const weightedMonsters: { monster: Creature; weight: number }[] = [];
  
  monsters.forEach(monster => {
    const tierIndex = tiers.indexOf(monster.tier!);
    if (tierIndex >= 0) {
      weightedMonsters.push({
        monster,
        weight: tierWeights[tierIndex]
      });
    }
  });
  
  if (weightedMonsters.length === 0) {
    return monsters[0].name; // Fallback
  }
  
  // Random selection weighted by tier weights
  const totalWeight = weightedMonsters.reduce((sum, wm) => sum + wm.weight, 0);
  let random = Math.random() * totalWeight;
  
  for (const { monster, weight } of weightedMonsters) {
    random -= weight;
    if (random <= 0) {
      return monster.name;
    }
  }
  
  return weightedMonsters[0].monster.name;
}

/**
 * Get all monster names from the bestiary
 */
export function getAllMonsterNames(): string[] {
  return BESTIARY.map(creature => creature.name);
}

/**
 * Get creature by name
 */
export function getCreature(name: string): Creature | undefined {
  return BESTIARY.find(creature => 
    creature.name.toLowerCase() === name.toLowerCase()
  );
}

/**
 * Get creature stats for a specific party size
 */
export function getCreatureStats(
  creatureName: string,
  partySize: 1 | 2 | 3 | 4
): CreatureStats | null {
  const creature = getCreature(creatureName);
  if (!creature) return null;
  
  return creature.statsByPartySize[partySize];
}

/**
 * Get all creature names
 */
export function getAllCreatureNames(): string[] {
  return BESTIARY.map(creature => creature.name);
}

